<template>
    <div v-if="!hideFilters" class="row q-col-gutter-md">
        <div class="col-12 col-md-3">
            <q-input
                v-model="filter.search"
                label="Search"
                dense
                outlined
                debounce="300"
                clearable
            >
                <template v-slot:append>
                    <q-icon name="search" />
                </template>
            </q-input>
        </div>
        <div class="col-12 col-md-3">
            <q-select
                v-model="filter.calendarId"
                :options="calendars"
                label="Calendar"
                dense
                outlined
                emit-value
                map-options
                clearable
            />
        </div>
        <div class="col-12 col-md-3">
            <q-select
                v-model="filter.congregationId"
                :options="congregations"
                label="Congregation"
                dense
                outlined
                emit-value
                map-options
                clearable
            />
        </div>
        <div class="col-12 col-md-3">
            <q-select
                v-model="filter.rightId"
                :options="rights"
                label="Right"
                dense
                outlined
                emit-value
                map-options
                clearable
            />
        </div>
    </div>

    <q-table
        class="sticky-container-table"
        :title="hideFilters ? '' : 'Users'"
        :rows="rows"
        :columns="columns"
        v-model:pagination="pagination"
        :loading="loading"
        @request="onRequest"
        binary-state-sort
        row-key="id"
        flat
        bordered
    >
        <template v-slot:body-cell-email="props">
            <q-td :props="props">
                {{ props.row.email }}
                <q-icon
                    v-if="props.row.isBouncer"
                    name="warning"
                    class="text-negative q-ml-sm"
                    title="Marked as Bouncer"
                />
            </q-td>
        </template>
        <template v-slot:body-cell-loginFailures="props">
            <q-td :props="props" class="text-center">
                <span
                    v-if="props.row.loginFailures > 0"
                    class="text-negative font-weight-bold"
                    >{{ props.row.loginFailures }}</span
                >
                <span v-else class="text-positive"
                    ><q-icon name="check"
                /></span>
            </q-td>
        </template>
        <template v-slot:body-cell-actions="props">
            <q-td :props="props" class="text-center">
                <div class="row q-gutter-sm justify-center">
                    <q-btn
                        size="sm"
                        color="secondary"
                        :href="'/dev/publisher/' + props.row.id"
                        title="Details"
                        icon="info"
                    />
                    <q-btn
                        size="sm"
                        color="warning"
                        @click="resetLoginBlock(props.row.id)"
                        title="Reset Login Block"
                        icon="lock_open"
                    />
                    <q-btn
                        size="sm"
                        color="info"
                        @click="resetEmailStatus(props.row.id)"
                        title="Reset Email Status"
                        icon="mark_email_read"
                    />
                </div>
            </q-td>
        </template>
    </q-table>
</template>

<script>
import { ref, onMounted, watch } from "vue";
import axios from "axios";
import { useQuasar } from "quasar";

export default {
    props: {
        initialSearch: String,
        initialCalendarId: [Number, String],
        initialCongregationId: [Number, String],
        calendars: Array,
        congregations: Array,
        rights: Array,
        hideFilters: {
            type: Boolean,
            default: false,
        },
    },
    setup(props) {
        const $q = useQuasar();
        const rows = ref([]);
        const loading = ref(false);
        const pagination = ref({
            sortBy: "userId",
            descending: false,
            page: 1,
            rowsPerPage: 10,
            rowsNumber: 10,
        });

        const filter = ref({
            search: props.initialSearch || "",
            calendarId:
                props.initialCalendarId !== null &&
                props.initialCalendarId !== ""
                    ? Number(props.initialCalendarId)
                    : null,
            congregationId:
                props.initialCongregationId !== null &&
                props.initialCongregationId !== ""
                    ? Number(props.initialCongregationId)
                    : null,
            rightId: null,
        });

        watch(
            () => props.initialSearch,
            (val) => {
                filter.value.search = val || "";
            },
        );
        watch(
            () => props.initialCalendarId,
            (val) => {
                filter.value.calendarId =
                    val !== null && val !== "" ? Number(val) : null;
            },
        );
        watch(
            () => props.initialCongregationId,
            (val) => {
                filter.value.congregationId =
                    val !== null && val !== "" ? Number(val) : null;
            },
        );

        const columns = [
            {
                name: "id",
                label: "ID",
                field: "id",
                sortable: true,
                align: "left",
            },
            {
                name: "firstName",
                label: "First Name",
                field: "firstName",
                sortable: true,
                align: "left",
            },
            {
                name: "lastName",
                label: "Last Name",
                field: "lastName",
                sortable: true,
                align: "left",
            },
            {
                name: "email",
                label: "Email",
                field: "email",
                sortable: true,
                align: "left",
            },
            {
                name: "right",
                label: "Right",
                field: "right",
                sortable: true,
                align: "left",
            },
            {
                name: "lastActivity",
                label: "Last Activity",
                field: "lastActivityHuman",
                sortable: true,
                align: "left",
            },
            {
                name: "loginFailures",
                label: "Login Failures",
                field: "loginFailures",
                sortable: true,
                align: "center",
            },
            {
                name: "actions",
                label: "Actions",
                field: "actions",
                align: "center",
            },
        ];

        const fetchFromServer = (
            start,
            limit,
            sortBy,
            descending,
            filterValues,
        ) => {
            const params = {
                start,
                limit,
                sortBy,
                descending,
                ...filterValues,
            };
            return axios.get("/dev/api/publishers", { params });
        };

        const onRequest = (props) => {
            const { page, rowsPerPage, sortBy, descending } = props.pagination;
            const filterValues = filter.value;

            loading.value = true;

            const start = (page - 1) * rowsPerPage;
            const limit = rowsPerPage;

            fetchFromServer(start, limit, sortBy, descending, filterValues)
                .then((response) => {
                    rows.value = response.data.rows;
                    pagination.value.rowsNumber = response.data.rowsNumber;

                    pagination.value.page = page;
                    pagination.value.rowsPerPage = rowsPerPage;
                    pagination.value.sortBy = sortBy;
                    pagination.value.descending = descending;

                    loading.value = false;
                })
                .catch((error) => {
                    loading.value = false;
                    $q.notify({
                        color: "negative",
                        message: "Failed to fetch publishers",
                        icon: "report_problem",
                    });
                });
        };

        const resetLoginBlock = (id) => {
            axios
                .post(`/dev/publisher/${id}/reset-login-block`)
                .then((response) => {
                    $q.notify({
                        color: "positive",
                        message: response.data.message || "Login block reset",
                        icon: "check",
                    });
                    onRequest({ pagination: pagination.value });
                })
                .catch((error) => {
                    $q.notify({
                        color: "negative",
                        message: "Failed to reset login block",
                        icon: "report_problem",
                    });
                });
        };

        const resetEmailStatus = (id) => {
            axios
                .post(`/dev/publisher/${id}/reset-email-status`)
                .then((response) => {
                    $q.notify({
                        color: "positive",
                        message: response.data.message || "Email status reset",
                        icon: "check",
                    });
                    onRequest({ pagination: pagination.value });
                })
                .catch((error) => {
                    $q.notify({
                        color: "negative",
                        message: "Failed to reset email status",
                        icon: "report_problem",
                    });
                });
        };

        // Watch filters to trigger reload
        watch(
            filter,
            () => {
                pagination.value.page = 1;
                onRequest({ pagination: pagination.value });
            },
            { deep: true },
        );

        onMounted(() => {
            onRequest({ pagination: pagination.value });
        });

        return {
            rows,
            columns,
            loading,
            pagination,
            filter,
            onRequest,
            resetLoginBlock,
            resetEmailStatus,
        };
    },
};
</script>
